import { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/api/client";
import { Loader2, X, CheckCircle2, Inbox, ClipboardCheck, Hourglass, Gauge } from "lucide-react";

const STATUS_LABEL = {
  pending_rubric: { label: "Rúbrica pendiente", cls: "bg-slate-100 text-slate-500" },
  pending_review: { label: "Pendiente de revisión", cls: "bg-amber-100 text-amber-700" },
  reviewed: { label: "Revisado", cls: "bg-emerald-100 text-emerald-700" },
};

export default function AdminReviews() {
  const [summary, setSummary] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [weekFilter, setWeekFilter] = useState("");
  const [openSubmission, setOpenSubmission] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [s, r] = await Promise.all([
        api.get("/admin/dashboard/activities-summary"),
        api.get("/admin/reviews", { params: weekFilter ? { week: weekFilter } : {} }),
      ]);
      setSummary(s.data);
      setReviews(r.data.reviews);
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  useEffect(() => { load(); }, [weekFilter]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-black text-[color:var(--text-primary)]">Revisiones · IAC</h1>
        <p className="text-sm text-[color:var(--text-secondary)]">Índice de Aplicabilidad de Contenidos = suma de 4 criterios / 16 × 100. No es una nota de desempeño ni un ranking de participantes.</p>
      </div>

      {error && <div className="text-rose-600 text-sm font-semibold">{error}</div>}

      {/* Resumen compacto IAC: por semana + global (solo revisados cuentan en los promedios) */}
      {summary && (
        <div className="bg-white rounded-2xl border border-[color:var(--brand-border)] shadow-[var(--shadow-sm)] overflow-hidden" data-testid="admin-iac-summary">
          <div className="px-6 py-5 flex items-center justify-between gap-4 border-b border-[color:var(--brand-border)] bg-[color:var(--surface-inset)]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[color:var(--brand-navy)] text-white flex items-center justify-center shrink-0"><Gauge className="w-5 h-5" /></div>
              <div>
                <span className="text-[10px] font-bold text-[color:var(--text-secondary)] uppercase block">IAC promedio general del programa</span>
                <div className="text-3xl font-black text-[color:var(--text-primary)] tabular-nums" data-testid="admin-iac-global-value">{summary.iac_global ?? "Sin datos"}</div>
              </div>
            </div>
            <p className="text-xs text-[color:var(--text-secondary)] max-w-xs text-right">
              Sustentado por {summary.iac_global_supported_by_weeks} de 5 talleres con revisiones registradas.
              {!summary.iac_global_is_definitive && <span className="block font-bold text-amber-600 mt-1">Aún no es definitivo: faltan revisiones en algunos talleres.</span>}
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[color:var(--text-secondary)] uppercase text-[10px] border-b border-[color:var(--brand-border)] bg-[var(--surface-2)]">
                  <th className="py-3 px-4">Actividad / Semana</th>
                  <th className="py-3 px-4"><span className="inline-flex items-center gap-1.5"><Inbox className="w-3.5 h-3.5" /> Entregas recibidas</span></th>
                  <th className="py-3 px-4"><span className="inline-flex items-center gap-1.5"><ClipboardCheck className="w-3.5 h-3.5" /> Revisados</span></th>
                  <th className="py-3 px-4"><span className="inline-flex items-center gap-1.5"><Hourglass className="w-3.5 h-3.5" /> Pendientes de revisión</span></th>
                  <th className="py-3 px-4">IAC prom. semana</th>
                </tr>
              </thead>
              <tbody>
                {summary.weeks.map((w) => {
                  const pending = Math.max(0, w.reviews_coverage.realizado - w.reviews_coverage.reviewed);
                  return (
                    <tr key={w.week} className="border-b border-[color:var(--brand-border)] hover:bg-[var(--surface-2)]" data-testid={`admin-iac-week-row-${w.week}`}>
                      <td className="py-3 px-4 font-bold text-[color:var(--text-primary)]">Semana {w.week} · {w.topic}</td>
                      <td className="py-3 px-4 font-semibold text-[color:var(--text-primary)]" data-testid={`admin-iac-week-${w.week}-recibidas`}>{w.realizado}</td>
                      <td className="py-3 px-4 font-semibold text-emerald-700">{w.reviews_coverage.reviewed}</td>
                      <td className="py-3 px-4" data-testid={`admin-iac-week-${w.week}-pendientes`}>
                        {pending > 0 ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-1 rounded-md bg-amber-100 text-amber-700">Pendiente de revisión ({pending})</span>
                        ) : (
                          <span className="text-[color:var(--text-secondary)]">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 font-black text-[color:var(--text-primary)]" data-testid={`admin-iac-week-${w.week}-promedio`}>
                        {w.avg_iac !== null ? w.avg_iac : <span className="text-[10px] font-bold uppercase text-[color:var(--text-secondary)]">Pendiente de revisión</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cola de revisión por entrega (sin ranking de participantes) */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-sm font-black text-[color:var(--text-primary)] uppercase tracking-wide">Cola de revisión</h2>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setWeekFilter("")} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${!weekFilter ? "bg-[color:var(--brand-navy)] text-white" : "bg-[var(--surface-inset)] text-[color:var(--text-secondary)] hover:bg-[color:var(--brand-border)]"}`}>Todas</button>
          {[1,2,3,4,5].map((w) => <button key={w} onClick={() => setWeekFilter(w)} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${weekFilter === w ? "bg-[color:var(--brand-navy)] text-white" : "bg-[var(--surface-inset)] text-[color:var(--text-secondary)] hover:bg-[color:var(--brand-border)]"}`}>Semana {w}</button>)}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[color:var(--brand-border)] shadow-[var(--shadow-sm)] overflow-x-auto" data-testid="reviews-table">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[color:var(--text-secondary)] uppercase text-[10px] border-b border-[color:var(--brand-border)] bg-[var(--surface-2)]"><th className="py-3 px-4">Participante</th><th>Semana</th><th>Estado</th><th>IAC</th><th></th></tr></thead>
          <tbody>
            {reviews && reviews.map((r) => {
              const st = STATUS_LABEL[r.review_status];
              return (
                <tr key={r.submission_id} className="border-b border-[color:var(--brand-border)] hover:bg-[var(--surface-2)]">
                  <td className="py-2.5 px-4 font-semibold text-[color:var(--text-primary)]">{r.participant_name}</td>
                  <td>{r.week}</td>
                  <td><span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-md ${st.cls}`}>{st.label}</span></td>
                  <td className="font-bold text-[color:var(--text-primary)]">{r.review_status === "reviewed" ? r.iac : <span className="text-[color:var(--text-secondary)] font-semibold">—</span>}</td>
                  <td><button disabled={r.review_status === "pending_rubric"} onClick={() => setOpenSubmission(r.submission_id)} className="text-brand-accent font-bold hover:underline disabled:text-slate-300 disabled:no-underline transition-colors" data-testid={`open-review-${r.submission_id}`}>Revisar</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {reviews && reviews.length === 0 && <p className="text-center text-xs text-[color:var(--text-secondary)] py-8">Sin entregas realizadas para revisar.</p>}
      </div>

      {openSubmission && <ReviewModal submissionId={openSubmission} onClose={() => setOpenSubmission(null)} onSaved={() => { setOpenSubmission(null); load(); }} />}
    </div>
  );
}

function ReviewModal({ submissionId, onClose, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [scores, setScores] = useState({});
  const [fundamento, setFundamento] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/admin/reviews/${submissionId}`).then((r) => {
      setDetail(r.data);
      if (r.data.review) { setScores(r.data.review.criteria_scores); setFundamento(r.data.review.fundamento || ""); }
    });
  }, [submissionId]);

  const save = async () => {
    setSaving(true); setError("");
    try {
      await api.put(`/admin/reviews/${submissionId}`, { criteria_scores: scores, fundamento });
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (!detail) return null;
  const f = detail.submission.fields || {};

  return (
    <div className="fixed inset-0 z-50 bg-[color:var(--brand-navy)]/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[88vh] overflow-y-auto p-6" data-testid="review-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-black text-[color:var(--text-primary)]">{detail.participant?.full_name} · Semana {detail.submission.week}</h2>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[var(--surface-inset)] transition-colors"><X className="w-5 h-5 text-[color:var(--text-secondary)]" /></button>
        </div>

        <div className="space-y-2 mb-5 text-xs">
          <p><b>Situación:</b> {f.situation}</p>
          <p><b>Qué usó:</b> {f.tool_used}</p>
          <p><b>Qué hizo:</b> {f.actions_taken}</p>
          <p><b>Resultado:</b> {f.outcome}</p>
          <p><b>Aprendizaje:</b> {f.reflection}</p>
        </div>

        {!detail.rubric ? (
          <p className="text-xs text-amber-600 font-semibold">La rúbrica de esta semana aún no está aprobada.</p>
        ) : (
          <div className="space-y-4">
            {detail.rubric.criteria.map((c) => (
              <div key={c.id}>
                <label className="text-xs font-bold text-[color:var(--text-primary)] block mb-1">{c.name}</label>
                <div className="flex gap-2">
                  {[0,1,2,3,4].map((v) => (
                    <button key={v} data-testid={`review-score-${c.id}-${v}`} onClick={() => setScores({ ...scores, [c.id]: v })} className={`w-9 h-9 rounded-lg text-xs font-black border transition-colors ${scores[c.id] === v ? "bg-[color:var(--brand-navy)] text-white border-[color:var(--brand-navy)]" : "bg-white text-[color:var(--text-secondary)] border-[color:var(--brand-border)] hover:bg-[var(--surface-2)]"}`}>{v}</button>
                  ))}
                </div>
              </div>
            ))}
            <div>
              <label className="text-xs font-bold text-[color:var(--text-primary)] block mb-1">Fundamento breve</label>
              <textarea rows={2} value={fundamento} onChange={(e) => setFundamento(e.target.value)} className="w-full text-xs border border-[color:var(--brand-border)] rounded-lg p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent" />
            </div>
            {error && <div className="text-xs font-semibold text-rose-600">{error}</div>}
            <button data-testid="review-save-btn" disabled={saving} onClick={save} className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl flex items-center gap-2 transition-colors">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Guardar revisión</button>
          </div>
        )}
      </div>
    </div>
  );
}
